/**
 * ConnectionValidationStatus - displays status of a single connection during validation
 */

import { CheckCircle2, XCircle, Circle, Clock, KeyRound } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { SessionConnectionStatus } from '../types/sessionTypes';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
// ============================================================================
// Types
// ============================================================================

/**
 * Visual status for connection validation
 */
export type ConnectionValidationState =
  | 'waiting'     // Not yet started
  | 'connecting'  // Currently validating
  | 'connected'   // Successfully connected
  | 'deferred'    // Config is fine; opened after the restore, not by this pass
  | 'failed';     // Failed to connect

interface ConnectionValidationStatusProps {
  /** Connection name to display */
  connectionName: string;
  /** Server/host info */
  serverInfo?: string;
  /** Current validation state */
  state: ConnectionValidationState;
  /** Error message if failed */
  error?: string;
  /**
   * Only meaningful with `state: 'deferred'` — whether the restore's background
   * sweep opens it (the default) or it waits for the first tab that needs it
   * because it has to ask for a password. Two different promises; saying
   * "connects on first use" about a connection that is already being opened,
   * or "opens in the background" about one that isn't, is the copy this splits.
   */
  deferredReason?: 'background' | 'password';
}

// ============================================================================
// Component
// ============================================================================

export function ConnectionValidationStatus({
  connectionName,
  serverInfo,
  state,
  error,
  deferredReason = 'background',
}: ConnectionValidationStatusProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 px-3 py-2 rounded-md transition-colors',
        state === 'failed' && 'bg-destructive/5'
      )}
      data-testid="connection-validation-status"
      data-state={state}
    >
      {/* Status Icon */}
      <div className="flex-shrink-0 mt-0.5">
        {state === 'waiting' && (
          <Circle className="h-5 w-5 text-muted-foreground" />
        )}
        {state === 'connecting' && (
          <RandomDotmSpinner size={20} className="text-primary" />
        )}
        {state === 'connected' && (
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-500" />
        )}
        {state === 'deferred' &&
          (deferredReason === 'password' ? (
            <KeyRound className="h-5 w-5 text-muted-foreground" />
          ) : (
            <Clock className="h-5 w-5 text-muted-foreground" />
          ))}
        {state === 'failed' && (
          <XCircle className="h-5 w-5 text-destructive" />
        )}
      </div>

      {/* Connection Info */}
      <div className="flex-1 min-w-0">
        <div className="space-y-0.5">
          <div
            className={cn(
              'font-medium text-sm leading-snug break-words whitespace-normal',
              state === 'waiting' && 'text-muted-foreground',
              state === 'connecting' && 'text-foreground',
              state === 'connected' && 'text-foreground',
              state === 'deferred' && 'text-foreground',
              state === 'failed' && 'text-foreground'
            )}
          >
            {connectionName}
          </div>
          {serverInfo && (
            <div className="text-xs text-muted-foreground break-all whitespace-normal">
              {serverInfo}
            </div>
          )}
        </div>

        {/* Status Text */}
        <div className="text-xs mt-0.5">
          {state === 'waiting' && (
            <span className="text-muted-foreground">Waiting...</span>
          )}
          {state === 'connecting' && (
            <span className="text-muted-foreground">Connecting...</span>
          )}
          {state === 'connected' && (
            <span className="text-green-600 dark:text-green-500">Connected</span>
          )}
          {state === 'deferred' && (
            <span className="text-muted-foreground">
              {deferredReason === 'password'
                ? 'Connects on first use — needs a password'
                : 'Opens in the background'}
            </span>
          )}
          {state === 'failed' && (
            <span className="text-destructive" title={error}>
              {error || 'Failed to connect'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Helper to convert SessionConnectionStatus to ConnectionValidationState
 */
export function toValidationState(
  status: SessionConnectionStatus,
  isCurrentlyValidating: boolean
): ConnectionValidationState {
  if (isCurrentlyValidating) {
    return 'connecting';
  }
  switch (status) {
    case 'connected':
      return 'connected';
    case 'disconnected':
      return 'failed';
    case 'connecting':
      return 'connecting';
    case 'unknown':
    default:
      return 'waiting';
  }
}
